"use client"

import { useReducedMotion } from "motion/react"
import {
  useEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react"
import { cn } from "@strivelabs/ui-core"
import { isEditableTarget } from "@strivelabs/ui-core/v2"
import { SIDEBAR_MAX_WIDTH, SIDEBAR_MIN_WIDTH, useSidebar } from "./context"
import { NavRail } from "./nav-rail"
import { NavTree } from "./nav-tree"
import type { NavSection } from "./types"

/** Icons-only rail, from the Home Shell design. */
const RAIL_WIDTH = 68

export interface SidebarProps {
  children: ReactNode
  className?: string
  /** Accessible name for the navigation landmark. */
  label?: string
}

/**
 * The sidebar shell. Animates between the expanded panel and the icon rail,
 * and owns the Cmd/Ctrl+B shortcut.
 */
export function Sidebar({ children, className, label = "Main" }: SidebarProps) {
  const { collapsed, toggleCollapsed, width, resizing } = useSidebar()

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "b" || !(event.metaKey || event.ctrlKey)) return
      // Bolding in a rich-text editor is also Cmd/Ctrl+B; the editor handles it
      // on its contenteditable and lets it bubble, so without this guard bolding
      // text would fold the sidebar.
      if (isEditableTarget(event.target)) return
      event.preventDefault()
      toggleCollapsed()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [toggleCollapsed])

  return (
    // Width animates in CSS rather than through motion: animating width in JS
    // forces a layout pass every frame, and the transition needs no spring.
    // `motion-reduce` drops it entirely for users who ask for that.
    <nav
      aria-label={label}
      data-collapsed={collapsed ? "true" : "false"}
      style={{
        width: collapsed ? RAIL_WIDTH : width,
        // A smooth ease-in-out for the fold rather than the design's entrance
        // ease-out (0.22, 1, 0.36, 1), which shoots the width ~90% of the way in
        // the first ~100ms and reads as a snap. This spreads the motion evenly.
        transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      className={cn(
        // Never clips: the resize handle sits outside the right edge, and the
        // org and account menus open past it. Content that would spill during
        // the width transition is clipped by the inner wrapper instead, so the
        // rail can stay narrow without swallowing its own popovers.
        // No edge border: the rail sits on the same white ground as the page
        // shell, and the content sheet beside it carries the hairline.
        "group/sidebar relative flex h-full shrink-0 flex-col overflow-visible bg-v2-sidebar",
        // The width transition is for collapsing. During a drag it would lag a
        // frame behind the pointer, so it is dropped until the drag ends.
        resizing ? "transition-none" : "transition-[width] [transition-duration:360ms] motion-reduce:transition-none",
        className,
      )}
    >
      {/* Clip horizontally in both states: during the width transition the
          expanded tree would otherwise render at the growing width and reflow
          its labels frame by frame (the fold "jank"). Clipping turns that into a
          clean left-to-right reveal. Menus/tooltips escape via their portals, so
          nothing that needs to overflow is caught here. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {children}
      </div>
      <ResizeHandle />
    </nav>
  )
}

/** How far one arrow-key press moves the edge. */
const KEYBOARD_STEP = 16

/**
 * Drag target for the sidebar width.
 *
 * The hit area is wider than the visible pill and straddles the border, because
 * a 4px target is unusable with a mouse. Double-click restores the default.
 *
 * Focusable and arrow-key driven as well: a pointer-only handle would make the
 * width the one part of the sidebar a keyboard cannot reach. This is the ARIA
 * window-splitter pattern — a focusable `separator` reporting its value.
 */
function ResizeHandle() {
  const reduce = useReducedMotion()
  const { collapsed, width, setWidth, resetWidth, resizing, setResizing } = useSidebar()
  const cleanupRef = useRef<(() => void) | null>(null)

  // A drag interrupted by unmount — or a pointer cancelled without a pointerup —
  // would otherwise leave the window listeners and the document's cursor /
  // userSelect overrides in place. Run whatever teardown the active drag left.
  useEffect(() => () => cleanupRef.current?.(), [])

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    const startX = event.clientX
    // The rendered width, not the stored one: dragging out of the rail has to
    // start from the rail's 56px or the panel jumps to its old width instantly.
    const startWidth = collapsed ? RAIL_WIDTH : width
    setResizing(true)
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"

    const onMove = (move: PointerEvent) => setWidth(startWidth + (move.clientX - startX))
    const onUp = () => {
      setResizing(false)
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      window.removeEventListener("pointercancel", onUp)
      cleanupRef.current = null
    }
    cleanupRef.current = onUp
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    window.addEventListener("pointercancel", onUp)
  }

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "ArrowRight":
        // From the rail, one press unfolds to the remembered width. Nudging by a
        // step instead would never clear the fold threshold, so the rail would
        // be a dead end for a keyboard.
        setWidth(collapsed ? width : width + KEYBOARD_STEP)
        break
      case "ArrowLeft":
        // At the minimum the next step folds, mirroring the drag gesture — a
        // step-down alone would clamp back to the minimum and stick there.
        if (collapsed) return
        setWidth(width <= SIDEBAR_MIN_WIDTH ? 0 : width - KEYBOARD_STEP)
        break
      case "Home":
        setWidth(SIDEBAR_MIN_WIDTH)
        break
      case "End":
        setWidth(SIDEBAR_MAX_WIDTH)
        break
      case "Enter":
      case " ":
        resetWidth()
        break
      default:
        return
    }
    event.preventDefault()
  }

  return (
    <div
      role="separator"
      tabIndex={0}
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      aria-valuenow={collapsed ? SIDEBAR_MIN_WIDTH : width}
      aria-valuemin={SIDEBAR_MIN_WIDTH}
      aria-valuemax={SIDEBAR_MAX_WIDTH}
      onPointerDown={onPointerDown}
      onDoubleClick={resetWidth}
      onKeyDown={onKeyDown}
      className="group absolute inset-y-0 -right-1 z-40 w-2 cursor-col-resize focus-visible:outline-none"
    >
      {/*
        The transition is inline rather than a utility class, which is also how
        the design does it. ui-blocks ships a broad global rule built on Tiptap's
        `--tt-transition-*` variables that outranks `transition-all` here, so the
        utility resolved to `transition-property: none` and the pill jumped. An
        inline declaration is the one thing a stylesheet rule cannot outrank.
      */}
      <span
        aria-hidden
        style={
          reduce
            ? undefined
            : { transition: "height 220ms ease-out, background-color 160ms ease-out" }
        }
        className={cn(
          "absolute left-1/2 top-1/2 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full",
          // Focus lights the pill the same way hover does — the wrapper drops
          // the default outline, so this is the only focus indicator there is.
          resizing
            ? "h-11 bg-v2-sidebar-ring"
            : "h-6 bg-v2-sidebar-foreground/15 group-hover:h-11 group-hover:bg-v2-sidebar-foreground/35 group-focus-visible:h-11 group-focus-visible:bg-v2-sidebar-ring",
        )}
      />
    </div>
  )
}

export function SidebarHeader({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("flex flex-col gap-1.5 px-2.5 pt-2", className)}>{children}</div>
}

export function SidebarContent({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-1 flex-col gap-0.5 overflow-y-auto px-2.5 py-1", className)}>
      {children}
    </div>
  )
}

export function SidebarFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    // No rule above it: the design separates the account row from the tree
    // with air alone.
    <div className={cn("flex flex-col gap-2 px-2.5 pb-1.5 pt-2.5", className)}>
      {children}
    </div>
  )
}

export interface SidebarBodyProps {
  sections: NavSection[]
}

/**
 * Renders the icon rail when collapsed and the full tree when expanded. The
 * only place that branches on `collapsed`, so NavTree and NavRail each stay
 * unaware of the other.
 */
export function SidebarBody({ sections }: SidebarBodyProps) {
  const { collapsed } = useSidebar()
  return collapsed ? <NavRail sections={sections} /> : <NavTree sections={sections} />
}
